import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  registerNewUserWithPasskey,
  isPasskeySupported,
  isPlatformAuthenticatorAvailable
} from '../services/passkeyService';

export const PasskeyRegister = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    deviceName: '',
    inviteCode: searchParams.get('inviteCode') || ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSupported, setIsSupported] = useState(true);
  const [hasAuthenticator, setHasAuthenticator] = useState(true);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    checkPasskeySupport();
  }, []);

  const checkPasskeySupport = async () => {
    const supported = isPasskeySupported();
    setIsSupported(supported);

    if (supported) {
      const available = await isPlatformAuthenticatorAvailable();
      setHasAuthenticator(available);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.displayName || !formData.email) {
      setError('Please fill in all required fields');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await registerNewUserWithPasskey(
        formData.email,
        formData.displayName,
        formData.deviceName || null,
        formData.inviteCode || null
      );

      if (result.success) {
        // Set user in context and store token
        setUser(result.user, result.token);

        // Navigate to home
        navigate('/');
      } else {
        setError(result.message || 'Registration failed. Please try again.');
      }
    } catch (err) {
      console.error('Passkey registration error:', err);
      setError(err.message || 'An error occurred during registration. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isSupported) {
    return (
      <div className="tennis-court min-h-screen flex items-center justify-center">
        <div className="max-w-md w-full px-4">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-4xl">⚠️</span>
              </div>
              <h2 className="text-2xl font-bold text-brown-800 mb-4">
                Browser Not Supported
              </h2>
              <p className="text-brown-600 mb-6">
                Your browser doesn't support passkeys. Please use a modern browser like Chrome, Safari, or Edge.
              </p>
              <Link
                to="/register"
                className="inline-block bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-300"
              >
                Use Email & Password Instead
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="tennis-court min-h-screen flex items-center justify-center">
      <div className="max-w-md w-full px-4">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-brown-100 rounded-full flex items-center justify-center">
              <span className="text-4xl">🔐</span>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-center text-brown-800 mb-2">
            Sign Up with Passkey
          </h2>

          <p className="text-center text-brown-600 mb-6 text-sm">
            Use Face ID, Touch ID, or Windows Hello for secure, password-free authentication
          </p>

          {!hasAuthenticator && (
            <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4 text-sm">
              <p className="font-bold">No biometric authenticator detected</p>
              <p className="mt-1">You can still register, but you'll need to use a security key or your phone for authentication.</p>
            </div>
          )}

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label htmlFor="displayName" className="block text-brown-700 text-sm font-bold mb-2">
                Name *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-brown-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                </div>
                <input
                  type="text"
                  id="displayName"
                  name="displayName"
                  className="shadow appearance-none border rounded-lg w-full py-3 pl-10 pr-4 text-brown-700 leading-tight focus:outline-none focus:shadow-outline focus:border-brown-500 transition-colors duration-300"
                  value={formData.displayName}
                  onChange={handleChange}
                  placeholder="Enter your name"
                  required
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="mb-4">
              <label htmlFor="email" className="block text-brown-700 text-sm font-bold mb-2">
                Email *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-brown-500" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                </div>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className="shadow appearance-none border rounded-lg w-full py-3 pl-10 pr-4 text-brown-700 leading-tight focus:outline-none focus:shadow-outline focus:border-brown-500 transition-colors duration-300"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  required
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="mb-4">
              <label htmlFor="deviceName" className="block text-brown-700 text-sm font-bold mb-2">
                Device Name (Optional)
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-brown-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 5a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2h-2.22l.123.489.804.804A1 1 0 0113 18H7a1 1 0 01-.707-1.707l.804-.804L7.22 15H5a2 2 0 01-2-2V5zm5.771 7H5V5h10v7H8.771z" clipRule="evenodd" />
                  </svg>
                </div>
                <input
                  type="text"
                  id="deviceName"
                  name="deviceName"
                  className="shadow appearance-none border rounded-lg w-full py-3 pl-10 pr-4 text-brown-700 leading-tight focus:outline-none focus:shadow-outline focus:border-brown-500 transition-colors duration-300"
                  value={formData.deviceName}
                  onChange={handleChange}
                  placeholder="e.g., iPhone, MacBook, Windows PC"
                  disabled={isLoading}
                />
              </div>
              <p className="text-xs text-brown-500 mt-1">
                Help identify this device in your security settings
              </p>
            </div>

            <div className="mb-4">
              <label htmlFor="inviteCode" className="block text-brown-700 text-sm font-bold mb-2">
                Invite Code (Optional)
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-brown-500" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                </div>
                <input
                  type="text"
                  id="inviteCode"
                  name="inviteCode"
                  className="shadow appearance-none border rounded-lg w-full py-3 pl-10 pr-4 text-brown-700 leading-tight focus:outline-none focus:shadow-outline focus:border-brown-500 transition-colors duration-300"
                  value={formData.inviteCode}
                  onChange={handleChange}
                  placeholder="Enter your invite code"
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="mb-6">
              <div className="flex items-center">
                <input
                  id="agree-terms"
                  name="agree-terms"
                  type="checkbox"
                  className="h-4 w-4 text-brown-600 focus:ring-brown-500 border-brown-300 rounded"
                  required
                  disabled={isLoading}
                />
                <label htmlFor="agree-terms" className="ml-2 block text-sm text-brown-700">
                  I agree to the{' '}
                  <Link to="/terms" className="text-brown-600 hover:text-brown-500 font-medium">
                    Terms and Conditions
                  </Link>
                </label>
              </div>
            </div>

            <div className="mb-6">
              <button
                type="submit"
                className={`w-full bg-brown-600 hover:bg-brown-700 text-white font-bold py-3 px-4 rounded-lg focus:outline-none focus:shadow-outline transition-colors duration-300 flex items-center justify-center ${
                  isLoading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Setting Up Passkey...
                  </>
                ) : (
                  <>
                    <span className="text-xl mr-2">🔐</span>
                    Create Account with Passkey
                  </>
                )}
              </button>
            </div>
          </form>

          <div className="mb-4">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Or use another method</span>
              </div>
            </div>
          </div>

          <div className="text-center space-y-2">
            <Link
              to="/register"
              className="block text-brown-600 hover:text-brown-500 font-medium text-sm"
            >
              Sign up with Email & Password
            </Link>
            <p className="text-brown-600 text-sm">
              Already have an account?{' '}
              <Link to="/login" className="text-brown-600 hover:text-brown-500 font-medium">
                Login
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasskeyRegister;
